// Profil-Nachbau F-25 Entwurf C (output/profil-team-20261014/cockpit-grafiken-20261019/SCHNITTSTELLEN-C.md 3.4; Entwurf c-quellen.html,
// c-plattform-quellen.html): der Teil „Quellen“ beider Cockpits. Oben das Ziel „Antworten mit Quelle“ (Ziel der Firma bzw. der Plattform,
// nie je Person) neben „Woher die Antworten kamen“ mit Strich für den gleich langen Zeitraum davor, darunter „Was als Nächstes hilft“ und
// die Karte „Antworten je Tag“ mit der Auswahl „Quelle“ (quelle=… in der Adresse); die Plattform zusätzlich „Antworten hilfreich“. Nur
// Anzahlen und Prozent, keine Inhalte, keine Links aus den Quellen; „Was gesucht wurde, steht hier nie.“ bleibt sichtbar.
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Target } from 'lucide-react';
import { errorMessage, type AdminContext } from '../api';
import { FormError, Loading, useResource } from '../components';
import { Aufklapper } from '../../pages/einstellungen/Bausteine';
import { BalkenListe, Block, type BalkenZeile } from './BerichtBausteine';
import { WerteTabelle, type Spalte } from './Bausteine';
import Diagramm, { diagrammSatz, type DiagrammReihe, type TippInhalt } from './Diagramm';
import { ZielBlock, ZielFestlegen, ZielFormular, Schritte } from './ZielBausteine';
import { beispielQuellenZiel, fortschritt, schritteQuellen, zielFestlegen, zielQuellen } from './ziel';
import { firmaZieleAdresse, firmaZielSetzen, PLATTFORM_ZIELE_WEG, plattformZielSetzen, ZIEL_VERALTET, type FirmaZiele, type PlattformZiele,
  type Ziel } from './zieleApi';
import { hilfreichAnteil } from './zielPlattform';
import { ALLGEMEIN, PLATTFORM_C, QUELLEN, QUELLEN_C, VERLAUF, ZIEL, ZIEL_REGELN } from './cockpitTexte';
import { abschnittText, datumWien, prozent, tippKopf, zahl } from './format';
import { firmaAdresse, plattformAdresse, quellenMitAnzahlen, QUALITAET_WEG, type Antwortqualitaet, type FirmaQuellen, type PlattformQuellen, type QuellenArt,
  type QuellenStand } from './cockpitApi';
import { vorzeitraumVon } from './berichtTeile';
import { heuteInWien, type Vorgabe, type Zeitraum, type ZeitraumWahl } from './zeitraum';

export type QuellenFilter = QuellenArt | 'alle';
export const ARTEN: QuellenArt[] = ['BUNDESRECHT', 'LANDESRECHT', 'JUDIKATUR', 'DOKUMENTE', 'WEB', 'RIS_SONSTIGE'];

/** quelle=… aus der Adresse; unbekannt oder fehlend: „alle“. */
export function quelleLesen(params: URLSearchParams): QuellenFilter {
  return ARTEN.find(a => a === params.get('quelle')) ?? 'alle';
}

/** Die Abfrage mit der neuen Quelle; „alle“ steht ohne Angabe; alle anderen Teile bleiben. */
export function quelleSetzen(params: URLSearchParams, wert: QuellenFilter): URLSearchParams {
  const neu = new URLSearchParams(params);
  if (wert === 'alle') neu.delete('quelle');
  else neu.set('quelle', wert);
  return neu;
}

/** Der gleich lange Zeitraum direkt davor als eigener Zeitraum (vorzeitraumVon aus berichtTeile.ts, wie am Server). */
export function vorzeitraumWahl(z: Pick<Zeitraum, 'von' | 'tage'>): ZeitraumWahl {
  return { art: 'EIGEN', ...vorzeitraumVon(z) };
}

/** Vergleich nur ohne „Dieses Jahr“ und mit Antworten davor (wie quellenWechsel, bericht.ts). */
function vergleichStand(vorgabe: Vorgabe | null, davor: QuellenStand | null): QuellenStand | null {
  return vorgabe !== 'dieses-jahr' && davor && davor.antworten > 0 ? davor : null;
}

type Ebene = 'firma' | 'plattform';
type Setzen = (wert: number | null, revision: number) => Promise<void>;
type Stand = QuellenStand & { zeitraum: Zeitraum };
const KEIN_ZIEL: Ziel = { wert: null, revision: 0 };
/** 409 STALE_ZIEL: Ziele neu laden, der Satz des Servers bleibt am Feld. */
const veraltet = (error: unknown) => (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code === ZIEL_VERALTET;

/** Zielblock „Antworten mit Quelle“ mit „Ziel ändern“ bzw. „Ziel festlegen“ und dem Formular (1 bis 100 %). Werte und Wörter aus
 *  zielQuellen (ziel.ts): der Balken zeigt den Anteil mit Quelle; auf der Plattform steht nur ein anderer Fuß. Nach dem Schließen geht der
 *  Fokus zurück auf den Knopf, der das Formular geöffnet hat. */
function QuellenZielBlock({ d, davor, ziel, darf, ebene, onSetzen }: { d: Stand; davor: QuellenStand | null; ziel: Ziel; darf: boolean;
  ebene: Ebene; onSetzen: Setzen }) {
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | undefined>(undefined);
  const [meldung, setMeldung] = useState('');
  // PF-SL5-zielfokus-1 (D-17): Fokus erst nach dem Commit zurück auf den Knopf des Formulars (wie PlattformCockpit.tsx).
  const [fokus, setFokus] = useState(false);
  const wurzel = useRef<HTMLDivElement>(null);
  const formId = `ki-q-ziel-form-${ebene}`;
  useEffect(() => {
    if (!fokus || offen) return;
    wurzel.current?.querySelector<HTMLButtonElement>(`[aria-controls="${formId}"]`)?.focus();
    setFokus(false);
  }, [fokus, offen, ziel.wert, formId]);
  const w = zielQuellen({ zeitraum: d.zeitraum, jetzt: d, davor, ziel: ziel.wert });
  const oeffnen = (neu: boolean) => { setMeldung(''); setFehler(undefined); setOffen(neu); };
  const schliessen = () => {
    setOffen(false);
    setFehler(undefined);
    setFokus(true);
  };
  const speichern = async (wert: number | null) => {
    setBusy(true);
    setFehler(undefined);
    try {
      await onSetzen(wert, ziel.revision);
      schliessen();
      setMeldung(ebene === 'firma' ? (wert === null ? ZIEL_REGELN.entfernt : ZIEL_REGELN.gespeichert) : (wert === null ? PLATTFORM_C.entfernt : PLATTFORM_C.gesetzt));
    } catch (error) {
      setFehler(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  // Ohne Ziel: Satz und Beispiel aus ziel.ts. Deren Sätze nennen die Firma („wie weit Ihre Firma ist“, „Ziele setzen Besitzer …“); auf der
  // Plattform deshalb wie bei den übrigen Plattform-Zielen (PlattformCockpit.tsx) „Noch kein Ziel festgelegt.“ und das Beispiel nur mit Recht.
  const f = zielFestlegen('quellen', beispielQuellenZiel(d.antworten > 0 ? 100 - (d.ohneTreffer.prozent ?? 0) : null), darf);
  return <div ref={wurzel}>
    <ZielBlock id={`ki-q-ziel-${ebene}`} titel={w.titel} gross={w.gross} von={w.von} prozent={w.prozent} skala={w.skala} satz={w.satz}
      fuss={ebene === 'firma' ? w.fuss : QUELLEN_C.fussPlattform}
      aktion={darf && ziel.wert !== null ? <button type="button" className="adm-button" aria-expanded={offen} aria-controls={formId}
        onClick={() => oeffnen(!offen)}><Target size={16} aria-hidden="true" />{ZIEL.aendern}</button> : undefined}>
      {ziel.wert === null && <ZielFestlegen text={ebene === 'firma' ? f.text : ZIEL.keinZiel}
        beispiel={ebene === 'firma' || darf ? f.beispiel : undefined} darf={darf} offen={offen} formularId={formId}
        onOeffnen={() => oeffnen(true)} />}
      {offen && <ZielFormular id={formId} label={ZIEL_REGELN.formular.quellen} wert={ziel.wert} min={1} max={100} einheit="%" busy={busy}
        fehler={fehler} entfernen onSpeichern={wert => { void speichern(wert); }} onAbbrechen={schliessen} />}
      {meldung && <p className="adm-success" role="status">{meldung}</p>}
    </ZielBlock>
  </div>;
}

/** Zeilen „Woher die Antworten kamen“ (PF-SL5-woher-1, Vergleich B1): Anteil, in Klammern die Zahl der Antworten mit Treffern aus der
 *  Quelle (am Server Anteil = Anzahl / Antworten, ganze Prozent), Strich für den Anteil davor. */
export function woherZeilen(d: QuellenStand<number | null>, vergleich: QuellenStand<number | null> | null): BalkenZeile[] {
  return [...d.arten].sort((a, b) => (b.prozent ?? 0) - (a.prozent ?? 0)).map(a => ({ key: a.art, name: a.name, prozent: a.prozent ?? 0,
    wert: prozent(a.prozent), anzahl: a.antworten, vorher: vergleich ? (vergleich.arten.find(x => x.art === a.art)?.prozent ?? 0) : null }));
}

/** Gemeinsamer Inhalt beider Cockpits: Ziel und „Woher die Antworten kamen“, Schritte, Karte „Antworten je …“ mit Auswahl „Quelle“. */
function QuellenInhalt({ d, davor, ziel, darf, ebene, onSetzen }: { d: Stand; davor: QuellenStand | null; ziel: Ziel; darf: boolean;
  ebene: Ebene; onSetzen: Setzen }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const vergleich = vergleichStand(d.zeitraum.vorgabe, davor);
  const mitVergleich = vergleich !== null;
  const zeilen = woherZeilen(d, vergleich);
  const waehlen = (neu: QuellenFilter) => setParams(vorher => quelleSetzen(vorher, neu), { state: location.state });
  return <>
    <div className="ki-teil-oben">
      <QuellenZielBlock d={d} davor={davor} ziel={ziel} darf={darf} ebene={ebene} onSetzen={onSetzen} />
      <Block id={`ki-q-woher-${ebene}`} titel={QUELLEN_C.woher} unterzeile={mitVergleich ? QUELLEN_C.woherUnter : QUELLEN_C.woherUnterOhneVergleich}>
        {d.antworten > 0 && <BalkenListe label={QUELLEN_C.woher} zeilen={zeilen} />}
        <p className="ki-hinweis">{QUELLEN_C.hinweis}</p>
      </Block>
    </div>
    {/* Die Schritte der Quellen tragen keinen Link (schritteQuellen in ziel.ts): die Seite ist schon „Quellen“. */}
    {d.antworten > 0 && <Schritte id={`ki-q-schritte-${ebene}`} schritte={schritteQuellen({ zeitraum: d.zeitraum, jetzt: d, davor, ziel: ziel.wert })
      .map(s => ({ key: s.key, symbol: s.symbol, stark: s.stark, text: s.text }))} />}
    {d.reihen && d.einheit && d.reihen.length > 0 && <QuellenKarte daten={d.reihen} einheit={d.einheit} ebene={ebene}
      quelle={quelleLesen(params)} waehlen={waehlen} />}
  </>;
}

/** Karte „Antworten je Tag/Woche/Monat“ (Spec 2.6): mit „Alle Quellen“ gestapelt „Mit Quelle“ und „Ohne Quelle“, mit einer Quelle eine
 *  Reihe „Antworten mit Treffern aus …“; darunter „Werte als Tabelle“ mit denselben Zahlen. */
function QuellenKarte({ daten, einheit, ebene, quelle, waehlen }: { daten: NonNullable<Stand['reihen']>; einheit: NonNullable<Stand['einheit']>;
  ebene: Ebene; quelle: QuellenFilter; waehlen: (neu: QuellenFilter) => void }) {
  const heute = heuteInWien(new Date());
  const titel = quelle === 'alle' ? QUELLEN_C.grafik[einheit] : QUELLEN_C.grafikQuelle(QUELLEN_C.arten[quelle], QUELLEN_C.je[einheit]);
  const reihen: DiagrammReihe[] = quelle === 'alle'
    ? [{ name: QUELLEN_C.mitQuelle, farbe: 'recherchen' }, { name: QUELLEN_C.ohneQuelle, farbe: 'ohneQuelle' }]
    : [{ name: QUELLEN_C.arten[quelle], farbe: 'recherchen' }];
  const abschnitte = daten.map(r => ({ von: r.von, bis: r.bis,
    werte: quelle === 'alle' ? [r.antworten - r.ohneQuelle, r.ohneQuelle] : [r.arten[quelle] ?? 0] }));
  const tipp = (i: number): TippInhalt => {
    const r = daten[i];
    const kopf = tippKopf(einheit, r.von, r.bis, heute);
    return quelle === 'alle'
      ? { kopf, zeilen: [{ farbe: 'recherchen', name: QUELLEN_C.mitQuelle, wert: zahl(r.antworten - r.ohneQuelle) },
        { farbe: 'ohneQuelle', name: QUELLEN_C.ohneQuelle, wert: zahl(r.ohneQuelle) }, { name: QUELLEN_C.antworten, wert: zahl(r.antworten) }] }
      : { kopf, zeilen: [{ farbe: 'recherchen', name: QUELLEN_C.arten[quelle], wert: zahl(r.arten[quelle] ?? 0) }] };
  };
  const spalten: Spalte[] = quelle === 'alle'
    ? [{ key: 'zeitraum', titel: VERLAUF.spalteZeitraum }, { key: 'mit', titel: QUELLEN_C.mitQuelle, zahl: true },
      { key: 'ohne', titel: QUELLEN_C.ohneQuelle, zahl: true }, { key: 'antworten', titel: QUELLEN_C.antworten, zahl: true }]
    : [{ key: 'zeitraum', titel: VERLAUF.spalteZeitraum }, { key: quelle, titel: QUELLEN_C.arten[quelle], zahl: true }];
  const tabelle = daten.map(r => ({ key: r.von, zellen: quelle === 'alle'
    ? [abschnittText(einheit, r.von, r.bis), zahl(r.antworten - r.ohneQuelle), zahl(r.ohneQuelle), zahl(r.antworten)]
    : [abschnittText(einheit, r.von, r.bis), zahl(r.arten[quelle] ?? 0)] }));
  return <Block id={`ki-q-grafik-${ebene}`} titel={titel} kopf={<label className="ki-quellen-wahl"><span>{QUELLEN_C.auswahl}</span>
    <select value={quelle} onChange={event => waehlen(ARTEN.find(a => a === event.target.value) ?? 'alle')}>
      <option value="alle">{QUELLEN_C.alle}</option>
      {ARTEN.map(art => <option key={art} value={art}>{QUELLEN_C.arten[art]}</option>)}
    </select></label>}>
    <p className="ki-quellen-legende" aria-hidden="true">
      {reihen.map(reihe => <span key={reihe.name} className="ki-d-legende-eintrag">
        <span className={`ki-d-schluessel ki-d-${reihe.farbe}`} />{reihe.name}</span>)}
    </p>
    <Diagramm art="saeulen" name={titel} einheit={einheit} abschnitte={abschnitte} reihen={reihen} heute={heute} wochenenden tipp={tipp}
      hoehe={{ breit: 'fenster', schmal: 210 }} vorlesen={diagrammSatz(titel, einheit, abschnitte)} />
    <Aufklapper titel={VERLAUF.tabelle}>
      <WerteTabelle label={VERLAUF.tabelle} spalten={spalten} leer={ALLGEMEIN.leer} zeilen={tabelle} />
    </Aufklapper>
  </Block>;
}

/** Ziel „Antworten hilfreich“ der Plattform (Q-7): Anteil „hilfreich“ der letzten 8 Wochen gegen die Schwelle des Servers (kein
 *  gespeichertes Ziel, deshalb kein Formular). Ohne Zeile mit Anteil kein Balken. */
function HilfreichBlock({ q: stand }: { q: { data?: Antwortqualitaet; error?: string; loading: boolean } }) {
  if (stand.loading) return <Loading text={ALLGEMEIN.laden} />;
  if (!stand.data) return <FormError message={stand.error} />;
  const q = stand.data;
  const { prozent: p, bewertungen } = hilfreichAnteil(q);
  const ziel = Math.round(q.settings.minRate * 100);
  const satz = [p === null ? QUELLEN_C.hilfreichSobald : '',
    QUELLEN_C.hilfreichRegel(q.minCell, prozent(ziel), q.settings.minCount, QUELLEN_C.tage(q.settings.days)),
    p === null ? QUELLEN_C.gezaehltSeitSatz(datumWien(q.since)) : ''].filter(Boolean).join(' ');
  return <>
    <ZielBlock id="ki-q-hilfreich" titel={QUELLEN_C.hilfreichTitel} gross={p === null ? '–' : prozent(p)}
      von={p === null ? QUELLEN_C.hilfreichKeine(q.weeks) : QUELLEN_C.hilfreichVon(zahl(bewertungen), bewertungen === 1, q.weeks)}
      prozent={p === null ? null : fortschritt(p, ziel)}
      skala={p === null ? undefined : { links: '0', mitte: QUELLEN_C.gezaehltSeit(datumWien(q.since)), rechts: QUELLEN_C.zielWert(ziel) }}
      satz={satz} art={p !== null && p < ziel ? 'knapp' : 'gut'} />
    {q.internal > 0 && <p className="ki-hinweis">{QUELLEN_C.intern(q.internal)}</p>}
  </>;
}

/** Teil „Quellen“ des Firmen-Cockpits: lädt Quellen, Vorzeitraum und Ziele selbst und meldet den Zeitraum. */
export function QuellenFirma({ firmaId, wahl, melden }: { firmaId: string; wahl: ZeitraumWahl; melden: (z: Zeitraum) => void }): JSX.Element {
  const [version, setVersion] = useState(0);
  const daten = useResource<FirmaQuellen>(firmaAdresse(firmaId, 'quellen', wahl));
  const z = daten.data?.zeitraum;
  const davor = useResource<FirmaQuellen>(z && z.vorgabe !== 'dieses-jahr' ? firmaAdresse(firmaId, 'quellen', vorzeitraumWahl(z)) : null);
  const ziele = useResource<FirmaZiele>(daten.data && quellenMitAnzahlen(daten.data) ? firmaZieleAdresse(firmaId) : null, version);
  useEffect(() => { if (z) melden(z); }, [z]); // eslint-disable-line react-hooks/exhaustive-deps
  // Während die Ziele nach dem Speichern neu laden, gilt die Antwort des Speicherns bzw. der letzte Stand derselben Firma (kein
  // Aufblitzen ohne Ziel und Knopf; der Fokus findet „Ziel ändern“ bzw. „Ziel festlegen“ schon).
  const zuletzt = useRef<{ firmaId: string; ziele: FirmaZiele } | null>(null);
  if (ziele.data) zuletzt.current = { firmaId, ziele: ziele.data };
  const stand = ziele.data ?? (zuletzt.current?.firmaId === firmaId ? zuletzt.current.ziele : undefined);
  if (daten.loading || (ziele.loading && !stand)) return <Loading text={ALLGEMEIN.laden} />;
  if (!daten.data) return <FormError message={daten.error} />;
  const setzen: Setzen = async (wert, expectedRevision) => {
    try {
      zuletzt.current = { firmaId, ziele: await firmaZielSetzen(firmaId, { art: 'ANTWORTEN_MIT_QUELLE', wert, expectedRevision }) };
    } catch (error) {
      if (veraltet(error)) setVersion(v => v + 1);
      throw error;
    }
    setVersion(v => v + 1);
  };
  if (!quellenMitAnzahlen(daten.data)) return <div className="ki-inhalt">
    <Block id="ki-q-woher-firma" titel={QUELLEN_C.woher} unterzeile={QUELLEN_C.woherUnterOhneVergleich}>
      <BalkenListe label={QUELLEN_C.woher} zeilen={woherZeilen(daten.data, null)} />
      <p className="ki-hinweis">{QUELLEN_C.hinweis}</p>
    </Block>
  </div>;
  return <div className="ki-inhalt">
    <QuellenInhalt d={daten.data} davor={davor.data && quellenMitAnzahlen(davor.data) ? davor.data : null} ziel={stand?.ziele.ANTWORTEN_MIT_QUELLE ?? KEIN_ZIEL}
      darf={stand?.darfSetzen === true} ebene="firma" onSetzen={setzen} />
  </div>;
}

/** Teil „Quellen“ des Plattform-Cockpits: wie die Firma mit dem Ziel der Plattform, dazu „Antworten hilfreich“ für alle, die den
 *  Betriebsstatus ansehen dürfen (Bereich „uebersicht“, wie PlattformCockpit.tsx). */
export function QuellenPlattform({ wahl, melden }: { wahl: ZeitraumWahl; melden: (z: Zeitraum) => void }): JSX.Element {
  const [version, setVersion] = useState(0);
  const daten = useResource<PlattformQuellen>(plattformAdresse('quellen', wahl));
  const z = daten.data?.zeitraum;
  const davor = useResource<PlattformQuellen>(z && z.vorgabe !== 'dieses-jahr' ? plattformAdresse('quellen', vorzeitraumWahl(z)) : null);
  const ziele = useResource<PlattformZiele>(PLATTFORM_ZIELE_WEG, version);
  const kontext = useResource<AdminContext>('/context?ebene=plattform');
  const betrieb = kontext.data?.platformAreas?.includes('uebersicht') === true;
  const qualitaet = useResource<Antwortqualitaet>(betrieb ? `${QUALITAET_WEG}?weeks=8` : null);
  useEffect(() => { if (z) melden(z); }, [z]); // eslint-disable-line react-hooks/exhaustive-deps
  // Wie bei der Firma: bis die Ziele neu geladen sind, gilt die Antwort des Speicherns bzw. der letzte Stand.
  const zuletzt = useRef<PlattformZiele | null>(null);
  if (ziele.data) zuletzt.current = ziele.data;
  const stand = ziele.data ?? zuletzt.current ?? undefined;
  if (daten.loading) return <Loading text={ALLGEMEIN.laden} />;
  if (!daten.data) return <FormError message={daten.error} />;
  const setzen: Setzen = async (wert, expectedRevision) => {
    try {
      zuletzt.current = await plattformZielSetzen({ art: 'ANTWORTEN_MIT_QUELLE', wert, expectedRevision });
    } catch (error) {
      if (veraltet(error)) setVersion(v => v + 1);
      throw error;
    }
    setVersion(v => v + 1);
  };
  return <div className="ki-inhalt">
    <QuellenInhalt d={daten.data} davor={davor.data ?? null} ziel={stand?.ziele.ANTWORTEN_MIT_QUELLE ?? KEIN_ZIEL}
      darf={stand?.darfSetzen === true} ebene="plattform" onSetzen={setzen} />
    {kontext.loading ? null : betrieb ? <HilfreichBlock q={qualitaet} /> : <p className="ki-info">{QUELLEN.keineQualitaet}</p>}
  </div>;
}
